import { useState, useEffect, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import {
  usePrenotaStore,
  useUploadAnexoStore,
  type FileUploadProgress,
} from "@PN/inclusao";

interface LocalAnexo {
  item: string;
  nomeArquivo: string;
  observacao: string;
  isNew?: boolean;
}

interface ConnectionStatus {
  isConnected: boolean;
  connectedCount: number;
  totalCount: number;
  text: string;
  color: "emerald" | "amber" | "rose";
}

interface FileValidation {
  isValid: boolean;
  errors: string[];
}

interface UploadConfig {
  maxFileSize: number; // em bytes
  allowedTypes: string[];
  maxFiles: number;
}

// Configurações de upload
const UPLOAD_CONFIG: UploadConfig = {
  maxFileSize: 10 * 1024 * 1024, // 10MB
  allowedTypes: [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
  ],
  maxFiles: 20,
};

export function useCompactAttachmentsDialog() {
  const draft = usePrenotaStore((state) => state.draft);
  const addFile = usePrenotaStore((state) => state.actions.aux.files.add);
  const removeFile = usePrenotaStore((state) => state.actions.aux.files.remove);
  const updateFileDescription = usePrenotaStore(
    (state) => state.actions.aux.files.updateDescription
  );
  const updateFileProgress = usePrenotaStore(
    (state) => state.actions.aux.files.updateProgress
  );
  const updateFileStatus = usePrenotaStore(
    (state) => state.actions.aux.files.updateStatus
  );
  const addAnexo = usePrenotaStore((state) => state.actions.draft.anexos.add);
  const removeAnexo = usePrenotaStore(
    (state) => state.actions.draft.anexos.remove
  );
  const updateAnexoDesc = usePrenotaStore(
    (state) => state.actions.draft.anexos.updateDesc
  );

  // Estados locais
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [localAnexos, setLocalAnexos] = useState<LocalAnexo[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileDescriptions, setFileDescriptions] = useState<Map<string, string>>(
    new Map()
  );
  const [idMapping, setIdMapping] = useState<Map<string, string>>(new Map());
  const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(
    new Map()
  );
  const [isUploading, setIsUploading] = useState(false);

  // Funções de validação
  const validateFile = useCallback((file: File): FileValidation => {
    const errors: string[] = [];

    // Verifica tamanho
    if (file.size > UPLOAD_CONFIG.maxFileSize) {
      errors.push(
        `Arquivo muito grande. Máximo: ${(
          UPLOAD_CONFIG.maxFileSize /
          1024 /
          1024
        ).toFixed(1)}MB`
      );
    }

    // Verifica tipo
    if (!UPLOAD_CONFIG.allowedTypes.includes(file.type)) {
      errors.push(`Tipo de arquivo não permitido: ${file.type}`);
    }

    // Verifica nome
    if (file.name.length > 100) {
      errors.push("Nome do arquivo muito longo (máximo 100 caracteres)");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, []);

  const validateFiles = useCallback(
    (
      files: File[]
    ): { valid: File[]; invalid: { file: File; errors: string[] }[] } => {
      const valid: File[] = [];
      const invalid: { file: File; errors: string[] }[] = [];

      files.forEach((file) => {
        const validation = validateFile(file);
        if (validation.isValid) {
          valid.push(file);
        } else {
          invalid.push({ file, errors: validation.errors });
        }
      });

      return { valid, invalid };
    },
    [validateFile]
  );

  // Sincroniza com a store quando abre o popover
  useEffect(() => {
    if (isPopoverOpen) {
      setLocalAnexos(
        draft.anexos.map((a) => ({
          item: a.item,
          nomeArquivo: a.nomeArquivo,
          observacao: a.observacao,
          isNew: false,
        }))
      );
    }
  }, [isPopoverOpen, draft.anexos]);

  // Handlers
  const handleFilesSelected = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);

      // Verifica se não excede o limite de arquivos
      const currentTotal = draft.anexos.length + selectedFiles.length;
      if (currentTotal + fileArray.length > UPLOAD_CONFIG.maxFiles) {
        toast.error(`Limite de ${UPLOAD_CONFIG.maxFiles} arquivos excedido`);
        return;
      }

      // Valida os arquivos
      const { valid, invalid } = validateFiles(fileArray);

      // Mostra erros de validação
      if (invalid.length > 0) {
        invalid.forEach(({ file, errors }) => {
          errors.forEach((error) => {
            toast.error(`${file.name}: ${error}`);
          });
        });
      }

      if (valid.length === 0) {
        if (invalid.length > 0) {
          toast.error("Nenhum arquivo válido selecionado");
        } else {
          toast.error("Nenhum arquivo selecionado");
        }
        return;
      }

      // Verifica duplicatas
      const existingNames = new Set([
        ...draft.anexos.map((a) => a.nomeArquivo),
        ...selectedFiles.map((f) => f.name),
      ]);

      const duplicates = valid.filter((file) => existingNames.has(file.name));
      if (duplicates.length > 0) {
        duplicates.forEach((file) => {
          toast.error(`Arquivo duplicado: ${file.name}`);
        });
      }

      const uniqueFiles = valid.filter((file) => !existingNames.has(file.name));
      if (uniqueFiles.length === 0) {
        toast.error("Todos os arquivos já foram adicionados");
        return;
      }

      setSelectedFiles((prev) => [...prev, ...uniqueFiles]);

      // Inicializa descrições vazias
      const newDescriptions = new Map(fileDescriptions);
      uniqueFiles.forEach((file) => {
        if (!newDescriptions.has(file.name)) {
          newDescriptions.set(file.name, "");
        }
      });
      setFileDescriptions(newDescriptions);

      toast.success(`${uniqueFiles.length} arquivo(s) adicionado(s)`);
    },
    [selectedFiles, validateFiles, fileDescriptions, draft.anexos]
  );

  const handleFileDescriptionChange = useCallback(
    (fileName: string, description: string) => {
      setFileDescriptions((prev) => new Map(prev).set(fileName, description));
    },
    []
  );

  const clearSelectedFiles = useCallback(() => {
    setSelectedFiles([]);
    setFileDescriptions(new Map());
  }, []);

  const removeSelectedFile = useCallback((fileName: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
    setFileDescriptions((prev) => {
      const newMap = new Map(prev);
      newMap.delete(fileName);
      return newMap;
    });
  }, []);

  const simulateUpload = useCallback(
    (item: string) => {
      let progress = 0;
      updateFileStatus(item, "uploading");

      const interval = setInterval(() => {
        progress += Math.random() * 15 + 5; // Entre 5 e 20%
        updateFileProgress(item, progress);

        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          updateFileStatus(item, "completed");
        }
      }, 200);
    },
    [updateFileProgress, updateFileStatus]
  );

  const handleUpload = useCallback(async () => {
    if (selectedFiles.length === 0 || isUploading) return;

    // Valida se todas as descrições foram preenchidas
    const missingDescriptions = selectedFiles.filter(
      (file) => !fileDescriptions.get(file.name)?.trim()
    );

    if (missingDescriptions.length > 0) {
      toast.error("Todos os arquivos devem ter uma descrição");
      return;
    }

    setIsUploading(true);

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const description = fileDescriptions.get(file.name) || "";

        // Gera ID sequencial
        const currentCount =
          draft.anexos.length + localAnexos.filter((a) => a.isNew).length;
        const item = String(currentCount + index + 1).padStart(3, "0");

        // Adiciona arquivo na store
        const autoId = addFile(file);
        setIdMapping((prev) => new Map(prev).set(item, autoId));

        // Cria anexo local
        const newAnexo: LocalAnexo = {
          item,
          nomeArquivo: file.name,
          observacao: description,
          isNew: true,
        };

        // Adiciona na store de anexos
        addAnexo({
          item: newAnexo.item,
          nomeArquivo: newAnexo.nomeArquivo,
          observacao: newAnexo.observacao,
        });

        // Adiciona na store de uploads
        useUploadAnexoStore.getState().addUpload({
          seq: item,
          doc: "",
          file,
        });

        // Atualiza estado local
        setLocalAnexos((prev) => [...prev, newAnexo]);

        // Simula upload
        simulateUpload(autoId);

        return newAnexo;
      });

      await Promise.all(uploadPromises);

      clearSelectedFiles();
      toast.success(
        `${selectedFiles.length} arquivo(s) adicionado(s) com sucesso`
      );
    } catch (error) {
      console.error("Erro no upload:", error);
      toast.error("Erro ao adicionar arquivos");
    } finally {
      setIsUploading(false);
    }
  }, [
    selectedFiles,
    fileDescriptions,
    isUploading,
    draft.anexos.length,
    localAnexos,
    addFile,
    addAnexo,
    simulateUpload,
    clearSelectedFiles,
  ]);

  const handleDownload = useCallback(
    (item: string) => {
      const uploadFiles = usePrenotaStore.getState().aux.uploadFiles || new Map();
      const autoId = idMapping.get(item);
      if (!autoId) {
        toast.error("Arquivo não encontrado para download");
        return;
      }

      const fileObj = uploadFiles.get(autoId)?.file;
      if (!fileObj) {
        toast.error("Arquivo não encontrado para download");
        return;
      }

      try {
        const url = URL.createObjectURL(fileObj);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          localAnexos.find((a) => a.item === item)?.nomeArquivo || "arquivo";
        document.body.appendChild(a);
        a.click();

        // Cleanup
        setTimeout(() => {
          URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);

        toast.success("Download iniciado");
      } catch (error) {
        console.error("Erro no download:", error);
        toast.error("Erro ao fazer download do arquivo");
      }
    },
    [idMapping, localAnexos]
  );

  const handleDelete = useCallback(
    (item: string) => {
      try {
        setLocalAnexos((prev) => prev.filter((a) => a.item !== item));
        removeAnexo(item);

        const autoId = idMapping.get(item);
        if (autoId) {
          removeFile(autoId);
          setIdMapping((prev) => {
            const newMap = new Map(prev);
            newMap.delete(item);
            return newMap;
          });
        }

        useUploadAnexoStore.getState().removeUpload(item);
        toast.success("Anexo removido com sucesso");
      } catch (error) {
        console.error("Erro ao remover anexo:", error);
        toast.error("Erro ao remover anexo");
      }
    },
    [idMapping, removeAnexo, removeFile]
  );

  const handleUpdateDescription = useCallback(
    (item: string, observacao: string) => {
      try {
        setLocalAnexos((prev) =>
          prev.map((a) => (a.item === item ? { ...a, observacao } : a))
        );
        updateAnexoDesc(item, observacao);
      } catch (error) {
        console.error("Erro ao atualizar descrição:", error);
        toast.error("Erro ao atualizar descrição");
      }
    },
    [updateAnexoDesc]
  );

  // Funções utilitárias
  const getFileProgress = useCallback(
    (anexo: LocalAnexo) => {
      const uploadFiles = usePrenotaStore.getState().aux.uploadFiles || new Map();
      const autoId = idMapping.get(anexo.item);
      return autoId ? uploadFiles.get(autoId) : undefined;
    },
    [idMapping]
  );

  const getConnectionStatus = useCallback(
    (anexo: LocalAnexo): ConnectionStatus => {
      const fileProgress = getFileProgress(anexo);
      const progress = fileProgress?.progress || 0;
      const isUploadingFile = fileProgress?.status === "uploading";
      const isCompleted = fileProgress?.status === "completed";

      return {
        isConnected: isCompleted,
        connectedCount: isCompleted ? 1 : 0,
        totalCount: 1,
        text: isUploadingFile
          ? "Enviando..."
          : isCompleted
          ? "Concluído"
          : "Pendente",
        color: isCompleted ? "emerald" : isUploadingFile ? "amber" : "rose",
      };
    },
    [getFileProgress]
  );

  // Valores computados
  const isUploadDisabled = useMemo(
    () =>
      selectedFiles.length === 0 ||
      selectedFiles.some((file) => !fileDescriptions.get(file.name)?.trim()) ||
      isUploading,
    [selectedFiles, fileDescriptions, isUploading]
  );

  const getUploadButtonText = useCallback(() => {
    if (isUploadDisabled) {
      return "Preencha descrições";
    }
    if (isUploading) {
      return "Adicionando...";
    }
    return `Adicionar ${selectedFiles.length} arquivo${
      selectedFiles.length !== 1 ? "s" : ""
    }`;
  }, [isUploadDisabled, isUploading, selectedFiles.length]);

  const totalFilesSize = useMemo(
    () => selectedFiles.reduce((total, file) => total + file.size, 0),
    [selectedFiles]
  );

  const canAddMoreFiles = useMemo(
    () => draft.anexos.length + selectedFiles.length < UPLOAD_CONFIG.maxFiles,
    [draft.anexos.length, selectedFiles.length]
  );

  return {
    // Estados
    isPopoverOpen,
    setIsPopoverOpen,
    localAnexos,
    selectedFiles,
    fileDescriptions,
    idMapping,
    draft,
    isUploading,

    // Handlers
    handleFilesSelected,
    handleFileDescriptionChange,
    clearSelectedFiles,
    removeSelectedFile,
    handleUpload,
    handleDownload,
    handleDelete,
    handleUpdateDescription,

    // Utilitários
    getFileProgress,
    getConnectionStatus,
    getUploadButtonText,
    validateFile,

    // Computed
    isUploadDisabled,
    totalFilesSize,
    canAddMoreFiles,
    uploadConfig: UPLOAD_CONFIG,
  };
}
